import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { Search, Briefcase, Loader2 } from "lucide-react";
import { get } from "@/lib/api";
import { JobStatusBadge } from "@/components/app/StatusChip";
import { MODULES, canSee } from "@/lib/nav";
import { useAuth } from "@/lib/auth";

function useDebounced(value, ms = 250) {
  const [v, setV] = useState(value);
  useEffect(() => {
    const t = setTimeout(() => setV(value), ms);
    return () => clearTimeout(t);
  }, [value, ms]);
  return v;
}

export function GlobalSearch() {
  const [q, setQ] = useState("");
  const [open, setOpen] = useState(false);
  const term = useDebounced(q.trim());
  const nav = useNavigate();
  const { user } = useAuth();
  const box = useRef(null);
  const { data, isFetching } = useQuery({
    queryKey: ["search", term],
    queryFn: () => get("/search", { q: term }),
    enabled: term.length >= 2,
  });

  useEffect(() => {
    const close = (e) => { if (box.current && !box.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", close);
    return () => document.removeEventListener("mousedown", close);
  }, []);

  const go = (job) => {
    setOpen(false);
    setQ("");
    nav(job.status === "draft" ? `/app/jobs/new?draft=${job.id}` : `/app/jobs/${job.id}/workspace`);
  };
  const jobs = data?.jobs || [];
  const pages = term.length >= 2 ? MODULES.filter((m) => canSee(m, user?.role) && m.label.toLowerCase().includes(term.toLowerCase())) : [];
  const goPage = (m) => { setOpen(false); setQ(""); nav(m.path); };

  return (
    <div ref={box} className="relative w-full max-w-md max-sm:min-w-0" data-tour="search">
      <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-ink-muted" />
      <input
        data-testid="global-search-input"
        value={q}
        onChange={(e) => { setQ(e.target.value); setOpen(true); }}
        onFocus={() => setOpen(true)}
        onKeyDown={(e) => e.key === "Enter" && jobs[0] && go(jobs[0])}
        placeholder="Search jobs and pages"
        className="h-9 w-full rounded-lg border border-line bg-subtle pl-9 pr-9 text-sm outline-none transition-colors duration-150 placeholder:text-ink-muted focus:border-brand/40 focus:bg-white"
      />
      {isFetching && <Loader2 size={14} className="absolute right-3 top-1/2 -translate-y-1/2 animate-spin text-ink-muted" />}
      {open && term.length >= 2 && (
        <div data-testid="global-search-results" className="absolute left-0 right-0 top-11 z-40 rounded-[6px] border border-line bg-white p-2 shadow-lg">
          {pages.map((m) => (
            <button key={m.key} data-testid={`search-page-${m.key}`} onClick={() => goPage(m)} className="flex w-full items-center gap-3 rounded-lg px-3 py-2 text-left text-sm transition-colors duration-150 hover:bg-subtle">
              <m.icon size={16} className="text-brand" /><span className="flex-1 truncate text-ink">{m.label}</span><span className="text-xs text-ink-muted">Page</span>
            </button>
          ))}
          {!jobs.length && !pages.length && !isFetching && <p className="px-3 py-2 text-sm text-ink-muted">Nothing matches "{term}"</p>}
          {jobs.map((j) => (
            <button
              key={j.id}
              data-testid={`search-result-${j.id}`}
              onClick={() => go(j)}
              className="flex w-full items-center gap-3 rounded-lg px-3 py-2 text-left text-sm hover:bg-subtle transition-colors duration-150"
            >
              <Briefcase size={16} className="text-ink-muted" />
              <span className="flex-1 truncate text-ink">{j.title || "Untitled job"}</span>
              <JobStatusBadge status={j.status} />
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
